import { useCallback, useEffect, useRef, useState } from 'react';
import { catalogFromList } from '../../services/git/repoCatalog';

/**
 * What "Link a code repository" can pick from (26.10.2026): the connected git
 * accounts — the same list "Clone a repository" offers (connectableAccounts:
 * GitHub, GitLab, Gitea … from the Git page) — narrowed to the ones whose
 * provider can list repositories, then that account's repositories and a
 * chosen repository's branches.
 *
 * Tokens stay where the Git page keeps them (OS keychain / the browser link);
 * every call goes through workspaceGitSync → the provider, which reads the
 * token itself. Nothing here sees or stores one. The git modules are imported
 * lazily: the projects page also runs in the browser build.
 */

/** Why a git API call failed, as one of: auth | rateLimit | network | other. */
export function gitApiErrorKind(err) {
  const status = err?.response?.status;
  const code = String(err?.code || err?.message || '');
  if (/_NOT_LINKED$/.test(code) || status === 401) return 'auth';
  const headers = err?.response?.headers || {};
  const remaining = headers['x-ratelimit-remaining'] ?? headers['ratelimit-remaining'];
  if (status === 429 || (status === 403 && String(remaining) === '0')) return 'rateLimit';
  if (err?.response === undefined && (err?.request || code === 'ECONNABORTED' || code === 'ERR_NETWORK' || /network|timeout/i.test(code))) return 'network';
  return 'other';
}

export default function useRepoSources() {
  const [sources, setSources] = useState(null); // null while looking
  const [sourceKey, setSourceKey] = useState('');
  const [account, setAccount] = useState(null);
  const [repos, setRepos] = useState([]);
  const [owners, setOwners] = useState([]); // [{ key, login, avatarUrl, type, count }]
  const [notices, setNotices] = useState([]); // why an organisation's repositories are hidden
  const [ownerKey, setOwnerKey] = useState('all');
  const [reposLoading, setReposLoading] = useState(false);
  const [reposError, setReposError] = useState(null); // { kind, provider }
  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [branchesError, setBranchesError] = useState(false);
  const reposSeq = useRef(0);
  const branchSeq = useRef(0);

  const source = (sources || []).find((s) => s.key === sourceKey) || null;

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [{ connectableAccounts }, { getProvider, can }] = await Promise.all([
          import('../GitConnectModal'),
          import('../../services/git/providers'),
        ]);
        const list = (await connectableAccounts()).filter((s) => {
          try {
            return can(getProvider(s.provider), 'listRepos');
          } catch {
            return false;
          }
        });
        if (!live) return;
        setSources(list);
        setSourceKey(list[0]?.key || '');
      } catch {
        if (live) setSources([]);
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  // The chosen account: its picture and login, then its repositories.
  useEffect(() => {
    if (!source) return undefined;
    let live = true;
    const seq = ++reposSeq.current;
    setAccount({ provider: source.provider, login: source.login, avatarUrl: '' });
    setRepos([]);
    setOwners([]);
    setNotices([]);
    setOwnerKey('all');
    setReposError(null);
    setReposLoading(true);
    (async () => {
      try {
        const { getProvider } = await import('../../services/git/providers');
        const acc = await getProvider(source.provider).getAccount({ provider: source.provider, accountId: source.accountId || undefined });
        if (live && acc?.connected) setAccount({ provider: source.provider, login: acc.login || source.login, avatarUrl: acc.avatarUrl || '' });
      } catch {
        // The picture is decoration; the provider icon stands in.
      }
    })();
    (async () => {
      try {
        const sync = await import('../../services/workspaceGitSync');
        // The user's AND their organisations' repositories (28.10.2026).
        const catalog = sync.listUserRepoCatalog
          ? await sync.listUserRepoCatalog(source.provider, source.accountId || undefined)
          : catalogFromList(await sync.listUserRepos(source.provider, source.accountId || undefined), { login: source.login });
        if (live && seq === reposSeq.current) {
          setRepos(Array.isArray(catalog?.repos) ? catalog.repos : []);
          setOwners(Array.isArray(catalog?.owners) ? catalog.owners : []);
          setNotices(Array.isArray(catalog?.notices) ? catalog.notices : []);
        }
      } catch (err) {
        if (live && seq === reposSeq.current) setReposError({ kind: gitApiErrorKind(err), provider: source.provider });
      } finally {
        if (live && seq === reposSeq.current) setReposLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [source]);

  const loadBranches = useCallback(
    async (repo) => {
      const seq = ++branchSeq.current;
      setBranches([]);
      setBranchesError(false);
      if (!repo || !source) {
        setBranchesLoading(false);
        return;
      }
      setBranchesLoading(true);
      try {
        const { listRepoBranches } = await import('../../services/workspaceGitSync');
        const list = await listRepoBranches(source.provider, source.accountId || undefined, repo.owner, repo.name);
        if (seq === branchSeq.current) setBranches(Array.isArray(list) ? list : []);
      } catch {
        if (seq === branchSeq.current) setBranchesError(true);
      } finally {
        if (seq === branchSeq.current) setBranchesLoading(false);
      }
    },
    [source]
  );

  return {
    sources,
    source,
    sourceKey,
    setSourceKey,
    account,
    repos,
    owners,
    notices,
    ownerKey,
    setOwnerKey,
    reposLoading,
    reposError,
    branches,
    branchesLoading,
    branchesError,
    loadBranches,
  };
}
